/**
 * IdentityCapture — D-Q7 portal identity sheet.
 *
 * Renders when a portal client clicks a review-bearing action item
 * (approve, sign, markup) and no actor_id exists in localStorage for this
 * portal session. Captures name + email, posts to the identity-capture
 * endpoint, and caches the returned actor_id.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const EMAIL_RE = /^[^\s@]{1,64}@[^\s@]+\.[^\s@]{2,}$/;
const NAME_MAX = 120;
const CONSENT_TEXT =
  'By continuing, you confirm that you are authorised to review this document and that your name and email may be recorded as part of the audit trail for this review.';

export interface IdentityCaptureResult {
  actorId: string;
  displayName: string;
}

interface IdentityCaptureProps {
  viewToken: string;
  /** Called after successful capture or "welcome back" confirm. */
  onCapture: (result: IdentityCaptureResult) => void;
  /** Called when user cancels — must prevent the pending write from proceeding. */
  onCancel: () => void;
  /** Base URL for the API (defaults to VITE_API_URL). */
  apiBase?: string;
}

function storageKey(viewToken: string) {
  return `portal_actor_${viewToken}`;
}

export function getStoredActorId(viewToken: string): string | null {
  try {
    const raw = localStorage.getItem(storageKey(viewToken));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.actorId ?? null;
  } catch {
    return null;
  }
}

export function storeActorId(viewToken: string, result: IdentityCaptureResult) {
  try {
    localStorage.setItem(storageKey(viewToken), JSON.stringify(result));
  } catch {
    // localStorage may be unavailable (private-mode iOS) — degrade gracefully
  }
}

export function IdentityCapture({
  viewToken,
  onCapture,
  onCancel,
  apiBase,
}: IdentityCaptureProps) {
  const base = apiBase ?? (import.meta.env.VITE_API_URL as string) ?? '';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  // Returning-session state
  const [returningActor, setReturningActor] = useState<IdentityCaptureResult | null>(null);
  const [lookupDone, setLookupDone] = useState(false);

  async function handleLookup() {
    const e = email.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) return;

    try {
      const res = await fetch(
        `${base}/api/portal/${viewToken}/identity-lookup?email=${encodeURIComponent(e)}`
      );
      if (res.ok) {
        const data = await res.json();
        if (data.found) {
          setReturningActor({ actorId: data.actor_id, displayName: data.display_name });
        }
      }
    } catch {
      // lookup failure is non-fatal — fall through to normal capture
    } finally {
      setLookupDone(true);
    }
  }

  function validate(): boolean {
    let ok = true;
    const n = name.trim();
    if (!n) { setNameError('Name is required'); ok = false; } else if (n.length > NAME_MAX) { setNameError(`Name must be ≤ ${NAME_MAX} characters`); ok = false; } else setNameError('');
    const e = email.trim();
    if (!e) { setEmailError('Email is required'); ok = false; } else if (!EMAIL_RE.test(e)) { setEmailError('Please enter a valid email address'); ok = false; } else setEmailError('');
    return ok;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setServerError('');

    try {
      const res = await fetch(`${base}/api/portal/${viewToken}/identity-capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim().toLowerCase() }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setServerError(data?.detail ?? 'Something went wrong. Please try again.');
        return;
      }

      const data = await res.json();
      const result: IdentityCaptureResult = { actorId: data.actor_id, displayName: data.display_name };
      storeActorId(viewToken, result);
      onCapture(result);
    } catch {
      setServerError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // Returning-session confirm
  if (returningActor) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold">Welcome back</h2>
          <p className="text-sm text-muted-foreground">
            Is this still you, <strong>{returningActor.displayName}</strong>?
          </p>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onCancel} disabled={submitting}>
              Not me
            </Button>
            <Button
              onClick={() => {
                storeActorId(viewToken, returningActor);
                onCapture(returningActor);
              }}
            >
              Yes, continue
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6 space-y-4">
        <h2 className="text-lg font-semibold">Before you continue</h2>
        <p className="text-sm text-muted-foreground">
          Please confirm your identity to proceed with this review.
        </p>

        <form onSubmit={handleSubmit} noValidate className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="ic-name">Full name</Label>
            <Input
              id="ic-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={NAME_MAX + 1}
              placeholder="Your name"
              autoComplete="name"
            />
            {nameError && <p className="text-xs text-destructive">{nameError}</p>}
          </div>

          <div className="space-y-1">
            <Label htmlFor="ic-email">Email address</Label>
            <Input
              id="ic-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setLookupDone(false);
                setReturningActor(null);
              }}
              onBlur={handleLookup}
              placeholder="you@example.com"
              autoComplete="email"
            />
            {emailError && <p className="text-xs text-destructive">{emailError}</p>}
          </div>

          <p className="text-xs text-muted-foreground leading-relaxed">{CONSENT_TEXT}</p>

          {serverError && <p className="text-sm text-destructive">{serverError}</p>}

          <div className="flex gap-3 justify-end pt-1">
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Continue'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
